import { useEffect, useRef, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { Card, Avatar, Button, Input } from '../../components/ui';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  developer: 'Developer',
  client: 'Client',
  agency_owner: 'Agency Owner',
  authenticated: 'User',
};

const roleLabel = (role?: string) =>
  role ? ROLE_LABELS[role] || role.charAt(0).toUpperCase() + role.slice(1) : '';

export default function ProfilePanel() {
  const { user } = useUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setEmail(user.email);
    const load = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('avatar, agencies:agency_id ( name )')
        .eq('id', user.id)
        .single();
      if (!error && data) {
        if (data.avatar) setAvatarUrl(data.avatar);
        const agency = Array.isArray(data.agencies) ? data.agencies[0] : data.agencies;
        if (agency?.name) setAgencyName(agency.name);
      }
    };
    load();
  }, [user]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setStatus(null);
      setIsError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (password && password !== confirmPassword) {
      setStatus('Passwords do not match');
      setIsError(true);
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setIsError(false);

    try {
      const updateAuth: { email?: string; password?: string } = {};
      if (email && email !== user.email) updateAuth.email = email;
      if (password) updateAuth.password = password;
      if (Object.keys(updateAuth).length > 0) {
        const { error } = await supabase.auth.updateUser(updateAuth);
        if (error) throw error;
      }

      let newAvatarUrl = avatarUrl;
      if (avatarFile) {
        const fileExt = avatarFile.name.split('.').pop();
        const filePath = `${user.id}/${user.id}-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, avatarFile, { upsert: true });
        if (uploadError) throw uploadError;
        const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
        newAvatarUrl = data.publicUrl;
      }

      const { error: updateError } = await supabase
        .from('users')
        .update({ name, avatar: newAvatarUrl })
        .eq('id', user.id);
      if (updateError) throw updateError;

      setAvatarUrl(newAvatarUrl);
      setAvatarFile(null);
      setAvatarPreview(null);
      setPassword('');
      setConfirmPassword('');
      setStatus('Profile updated');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : String(err));
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card eyebrow="Profile" title={user?.name || 'Profile'}>
      {status && (
        <p
          style={{
            margin: '0 0 var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: isError ? 'var(--danger-500)' : 'var(--status-up)',
          }}
        >
          {status}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'flex',
            gap: '18px',
            alignItems: 'flex-start',
            marginBottom: 'var(--space-5)',
          }}
        >
          <Avatar name={user?.name || ''} src={avatarPreview || avatarUrl} size={56} />
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            <div>
              {roleLabel(user?.role)}
              {agencyName ? ` · ${agencyName}` : ''}
            </div>
            <div className="t-num" style={{ color: 'var(--text-faint)' }}>
              {user?.email}
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              icon="pencil"
              style={{ marginTop: '8px', marginLeft: '-10px' }}
              onClick={() => fileInputRef.current?.click()}
            >
              Change Avatar
            </Button>
            {avatarFile && (
              <div className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {avatarFile.name}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2"
          style={{ gap: '14px', marginBottom: 'var(--space-5)' }}
        >
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="New Password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            label="Confirm Password"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <Button type="submit" icon="check" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Profile'}
        </Button>
      </form>
    </Card>
  );
}
